import { allGuides } from 'content-collections'
import { type ComponentType, lazy, Suspense } from 'react'
import { data } from 'react-router'
import { mdxComponents } from '#app/components/brand/mdx.tsx'
import {
	findVisible,
	getMdxLoader,
	toFrontmatter,
	type Frontmatter,
	type MdxLoader,
} from '#app/utils/content.ts'
import {
	articleAttribution,
	buildMeta,
	DEFAULT_OG_IMAGE,
	SITE_URL,
} from '#app/utils/seo.ts'
import { ArticleLayout } from '../components/article-layout'
import { MarketingErrorBoundary } from '../components/marketing-boundary'
import { type Route } from './+types/$slug.ts'

// Lazy: each article is its own chunk (real ES module — no eval), loaded only
// for the requested slug.
const LOADERS = import.meta.glob('/content/guides/*/index.mdx') as Record<
	string,
	MdxLoader
>

type ArticleComponent = ComponentType<{ components?: typeof mdxComponents }>
const cache = new Map<string, ArticleComponent>()
function articleFor(slug: string): ArticleComponent | null {
	const load = getMdxLoader(LOADERS, slug)
	if (!load) return null
	let Comp = cache.get(slug)
	if (!Comp) {
		Comp = lazy(load) as ArticleComponent
		cache.set(slug, Comp)
	}
	return Comp
}

function articleJsonLd(frontmatter: Frontmatter, url: string) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: frontmatter.title,
		description: frontmatter.description,
		...articleAttribution(frontmatter.author),
		...(frontmatter.date ? { datePublished: frontmatter.date } : {}),
		...(frontmatter.updated || frontmatter.date
			? { dateModified: frontmatter.updated ?? frontmatter.date }
			: {}),
		image: `${SITE_URL}${frontmatter.image ?? DEFAULT_OG_IMAGE}`,
		url,
		mainEntityOfPage: url,
	}
}

// FAQPage rich result — same answer strings the page renders (see FaqSection).
function faqJsonLd(frontmatter: Frontmatter) {
	if (!frontmatter.faqs?.length) return null
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: frontmatter.faqs.map((f) => ({
			'@type': 'Question',
			name: f.q,
			acceptedAnswer: { '@type': 'Answer', text: f.a },
		})),
	}
}

function breadcrumbJsonLd(title: string, url: string) {
	const origin = 'https://competitorbacklinks.com'
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: origin },
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Guides',
				item: `${origin}/guides`,
			},
			{ '@type': 'ListItem', position: 3, name: title, item: url },
		],
	}
}

export async function loader({ params }: Route.LoaderArgs) {
	const doc = findVisible(allGuides, params.slug)
	if (!doc) throw new Response('Not found', { status: 404 })
	return data({ slug: doc.slug, frontmatter: toFrontmatter(doc) })
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data) return []
	const path = `/guides/${data.slug}`
	const url = `https://competitorbacklinks.com${path}`
	const faq = faqJsonLd(data.frontmatter)
	return [
		...buildMeta({
			title: `${data.frontmatter.title} | Competitor Backlinks`,
			description: data.frontmatter.description,
			path,
			image: data.frontmatter.image,
			type: 'article',
		}),
		{ 'script:ld+json': articleJsonLd(data.frontmatter, url) },
		{ 'script:ld+json': breadcrumbJsonLd(data.frontmatter.title, url) },
		...(faq ? [{ 'script:ld+json': faq }] : []),
	]
}

export default function GuideArticle({ loaderData }: Route.ComponentProps) {
	const Article = articleFor(loaderData.slug)
	// Metadata resolved (loader didn't 404) but no compiled body — never happens
	// with aligned globs, but fail loudly rather than render a blank page.
	if (!Article)
		throw new Error(`Missing MDX body for guide "${loaderData.slug}"`)
	return (
		<ArticleLayout
			frontmatter={loaderData.frontmatter}
			backTo="/guides"
			backLabel="All guides"
		>
			<Suspense fallback={<div className="h-64" aria-hidden />}>
				<Article components={mdxComponents} />
			</Suspense>
		</ArticleLayout>
	)
}

export function ErrorBoundary() {
	return (
		<MarketingErrorBoundary
			notFoundTitle="We couldn’t find that guide"
			notFoundBody="It may have moved or been renamed. Browse the rest of the guides instead."
		/>
	)
}
